import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DATA_SOURCE_REF, isDynamic, DataSourceModeToggle, DynamicDataSourceField } from '../dataSourceBinding';
import { WaLabel, WaOptionList, optionalLabel } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { RadioButtonsGroupCore } from '../../../../flow-core/components/RadioButtonsGroupCore';

// Auto-generate a stable, unique option id from its human-readable title so users
// never have to author internal ids by hand. Slug the title (fallback "option")
// and append a counter until unique within the list. Generated once at add-time.
const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const generateOptionId = (title, existingIds) => {
  const base = slugify(title) || 'option';
  let candidate = base;
  let n = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  return candidate;
};

export const RadioButtonsGroupComponent = new FlowComponent({
  ...RadioButtonsGroupCore,
  renderPreview(properties) {
    return (
      <Box sx={{ mt: 2, opacity: properties.visible === false ? 0.5 : 1 }}>
        <WaLabel>{optionalLabel(properties.label || 'Choose one option', properties.required)}</WaLabel>
        {properties.description && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: -0.75, mb: 0.5 }}>{properties.description}</Typography>
        )}
        {isDynamic(properties) ? (
          <Typography variant="caption" color="text.secondary">
            {`⚡ options from ${properties[DATA_SOURCE_REF]}`}
          </Typography>
        ) : (
          <WaOptionList options={properties['data-source'] || []} control="radio" />
        )}
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange, context) {
    const isDynamicFlow = context?.flowType === 'DYNAMIC' || context?.isDynamicFlow;

    const handleArrayItemChange = (index, field, value) => {
      const currentArray = properties['data-source'] || [];
      const newArray = [...currentArray];
      newArray[index] = { ...newArray[index], [field]: value };
      onChange('data-source', newArray);
    };

    const handleAddArrayItem = () => {
      const currentArray = properties['data-source'] || [];
      const title = 'New Option';
      const newItem = { id: generateOptionId(title, currentArray.map((o) => o.id)), title };
      onChange('data-source', [...currentArray, newItem]);
    };

    const handleRemoveArrayItem = (index) => {
      const currentArray = properties['data-source'] || [];
      const newArray = currentArray.filter((_, i) => i !== index);
      onChange('data-source', newArray);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
        />

        <FormControlLabel
          control={
            <Switch
              checked={properties.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />

        {/* Options */}
        <Box>
          <Typography variant="body2" gutterBottom>
            Options
          </Typography>
          {isDynamicFlow && <DataSourceModeToggle properties={properties} onChange={onChange} />}
          {isDynamicFlow && isDynamic(properties) ? (
            <DynamicDataSourceField properties={properties} onChange={onChange} />
          ) : (
            <>
              {(properties['data-source'] || []).map((item, index) => (
                <Box
                  key={index}
                  sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Title"
                      value={item.title || ''}
                      onChange={(e) => handleArrayItemChange(index, 'title', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveArrayItem(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddArrayItem}
                variant="outlined"
              >
                Add Option
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  },
});
